import mongoose from "mongoose";
import { z } from "zod";

const isValidId = (id) => mongoose.Types.ObjectId.isValid(id);

export const InvestmentValidationSchema = z.object({
  name: z
    .string()
    .min(1, { message: "Name is required." })
    .transform((v) => v.trim()),
  slug: z
    .string()
    .min(1, { message: "Slug is required." })
    .trim()
    .transform(
      (slug) =>
        slug
          .toLowerCase()
          .replace(/[^a-z0-9\s-]/g, "") // Remove invalid characters
          .replace(/\s+/g, "-") // Replace spaces with hyphens
          .replace(/-+/g, "-") // Replace multiple hyphens with single hyphen
          .replace(/^-|-$/g, "") // Remove leading or trailing hyphens
    )
    .refine((slug) => /^[a-z0-9-]+$/.test(slug), {
      message:
        "Slug must contain only lowercase letters, numbers, and hyphens.",
    }),
  short_description: z
    .string()
    .min(1, { message: "Short description is required." })
    .transform((v) => v.trim()),
  featured_image: z
    .string()
    .nullable()
    .refine((v) => v === null || isValidId(v), {
      message: "Featured image must be a valid ObjectId or null.",
    }),
  banner_image: z
    .string()
    .nullable()
    .refine((v) => v === null || isValidId(v), {
      message: "Banner image must be a valid ObjectId or null.",
    }),
  info_image: z
    .string()
    .nullable()
    .refine((v) => v === null || isValidId(v), {
      message: "Info image must be a valid ObjectId or null.",
    }),
  description: z
    .string()
    .default("")
    .transform((v) => v.trim()),
  button_label: z
    .string()
    .default("")
    .transform((v) => v.trim()),
  order_number: z
    .string()
    .transform((val) => val.trim() || "1")
    .refine((val) => !isNaN(Number(val)) && Number.isInteger(Number(val)), {
      message: "Order number must be a valid number",
    })
    .refine((val) => Number(val) > 0, {
      message: "Order number must be greater than 0",
    }),

  feature_heading: z
    .string()
    .default("")
    .transform((v) => v.trim()),

  key_points_heading: z
    .string()
    .default("")
    .transform((v) => v.trim()),
  key_points_description: z
    .string()
    .default("")
    .transform((v) => v.trim()),

  packages_heading: z
    .string()
    .default("")
    .transform((v) => v.trim()),
  packages_short_description: z
    .string()
    .default("")
    .transform((v) => v.trim()),

  registration_contact_heading: z
    .string()
    .default("")
    .transform((v) => v.trim()),
  registration_contact_sub_heading: z
    .string()
    .default("")
    .transform((v) => v.trim()),
  registration_contact_email_1: z
    .string()
    .default("")
    .transform((v) => v.trim())
    .refine((v) => v === "" || z.string().email().safeParse(v).success, {
      message: "Must be a valid email address",
    }),
  registration_contact_email_2: z
    .string()
    .default("")
    .transform((v) => v.trim())
    .refine((v) => v === "" || z.string().email().safeParse(v).success, {
      message: "Must be a valid email address",
    }),

  company_type_heading: z
    .string()
    .default("")
    .transform((v) => v.trim()),

  important_info_heading: z
    .string()
    .default("")
    .transform((v) => v.trim()),

  meta_title: z
    .string()
    .default("")
    .transform((v) => v.trim()),
  meta_image: z
    .string()
    .nullable()
    .refine((value) => value === null || isValidId(value), {
      message: "Meta Image must be a valid ObjectId or null.",
    })
    .default(null),
  meta_keywords: z
    .string()
    .default("")
    .transform((v) => v.trim()),
  meta_description: z
    .string()
    .default("")
    .transform((v) => v.trim()),
});
